import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { date: "", code: "", plateforme: "Airbnb", logement: "", nuits: 1, loyer: "", frais_menage: 0, commission: 0, taxes_versees: 0, th_percue: 0, total_verse: "" };
const NUM = [["nuits", "Nuits"], ["loyer", "Loyer ($)"], ["frais_menage", "Frais de ménage ($)"], ["commission", "Commission ($)"], ["taxes_versees", "Taxes remises ($)"], ["th_percue", "Taxe de séjour perçue ($)"], ["total_verse", "Total versé ($)"]];

export const ReservationDialog = ({ open, initial, onClose, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["nuits", "loyer", "frais_menage", "commission", "taxes_versees", "th_percue"].forEach((k) => (payload[k] = Number(payload[k]) || 0));
    payload.total_verse = form.total_verse === "" || form.total_verse === null ? null : Number(form.total_verse);
    setLoading(true);
    try {
      if (initial?.id) await api.put(`/reservations/${initial.id}`, payload);
      else await api.post("/reservations", payload);
      toast.success(initial?.id ? "Réservation modifiée" : "Réservation ajoutée");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="reservation-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Modifier la réservation" : "Saisie manuelle d'une réservation"}</DialogTitle>
          <DialogDescription>Laissez « Total versé » vide pour le calculer (loyer + ménage + taxes − commission).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Date</Label><DateField required value={form.date} onChange={(v) => setForm({ ...form, date: v })} className="w-full" data-testid="res-form-date" /></div>
          <div className="space-y-1"><Label>Code</Label><Input required value={form.code} onChange={set("code")} data-testid="res-form-code" /></div>
          <div className="space-y-1">
            <Label>Plateforme</Label>
            <Select value={form.plateforme} onValueChange={(v) => setForm({ ...form, plateforme: v })}>
              <SelectTrigger data-testid="res-form-plateforme"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Airbnb" data-testid="res-form-plateforme-airbnb">Airbnb</SelectItem>
                <SelectItem value="Booking" data-testid="res-form-plateforme-booking">Booking</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Logement</Label><Input value={form.logement} onChange={set("logement")} data-testid="res-form-logement" /></div>
          {NUM.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label>{label}</Label>
              <Input type="number" step={k === "nuits" ? "1" : "0.01"} min="0" required={k === "loyer"} value={form[k] ?? ""} onChange={set(k)} data-testid={`res-form-${k}`} />
            </div>
          ))}
          <DialogFooter className="col-span-2 mt-2">
            <Button type="submit" disabled={loading} className="bg-emerald-700 hover:bg-emerald-800" data-testid="res-form-submit">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
